import React, { useEffect, useState } from 'react';
import {
    Box,
    CardContent,
    Typography,
    TextField,
    Button,
    Alert,
    Grid,
    MenuItem,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton
} from '@mui/material';
import { useParams, useHistory } from 'react-router-dom';
import {
    IconBuilding,
    IconDeviceFloppy,
    IconTrash,
    IconPlus,
    IconEdit,
    IconBuildingSkyscraper,
    IconBuildingBank
} from '@tabler/icons';
import api from '../../../../services/api';
import PageHeader from '../../../../../ui-component/extended/PageHeader';
import GlassCard from '../../../../../ui-component/extended/GlassCard';
import GradientIconBadge from '../../../../../ui-component/extended/GradientIconBadge';

const ACCOUNT_TYPE_LABEL = { checking: 'Conta Corrente', savings: 'Poupança' };

const FIELDS = [
    { name: 'name', label: 'Nome', required: true },
    { name: 'cnpj', label: 'CNPJ' },
    { name: 'phone', label: 'Telefone' },
    { name: 'email', label: 'Email', type: 'email' },
    { name: 'address', label: 'Endereço' }
];

const EditCompany = () => {
    const { id } = useParams();
    const history = useHistory();

    const [company, setCompany] = useState({ name: '', cnpj: '', phone: '', email: '', address: '' });
    const [branches, setBranches] = useState([]);
    const [bankAccounts, setBankAccounts] = useState([]);
    const [banks, setBanks] = useState([]);
    const [agencies, setAgencies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [appId, setAppId] = useState(null);
    const [status, setStatus] = useState({ type: '', message: '' });
    const [saving, setSaving] = useState(false);
    const [bankDialogOpen, setBankDialogOpen] = useState(false);
    const [bankForm, setBankForm] = useState({ bank: '', agency: '', account_number: '', account_digit: '', account_type: 'checking' });

    useEffect(() => {
        (async () => {
            try {
                const res = await api.get('v1/apps/default/');
                setAppId(res.data.app.id);
            } catch (error) {
                console.error(error);
                setStatus({ type: 'error', message: 'Erro ao buscar app padrão' });
            }
        })();
    }, []);

    useEffect(() => {
        if (!appId || !id) return;
        fetchCompany();
        fetchBranches();
        fetchBankAccounts();
        fetchBanks();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId, id]);

    const fetchCompany = async () => {
        try {
            const response = await api.get(`v1/apps/${appId}/companies/${id}/`);
            setCompany(response.data);
            setLoading(false);
        } catch (error) {
            console.error('Erro ao buscar empresa:', error);
            setLoading(false);
        }
    };

    const fetchBranches = async () => {
        try {
            const response = await api.get(`v1/apps/${appId}/branches/?company=${id}`);
            setBranches(response.data.branches || []);
        } catch (error) {
            console.error('Erro ao buscar filiais:', error);
        }
    };

    const fetchBankAccounts = async () => {
        try {
            const response = await api.get(`v1/apps/${appId}/bank-accounts/?company=${id}`);
            setBankAccounts(response.data.bank_accounts || []);
        } catch (error) {
            console.error('Erro ao buscar contas bancárias:', error);
        }
    };

    const fetchBanks = async () => {
        try {
            const response = await api.get(`v1/apps/${appId}/banks/`);
            setBanks(response.data || []);
        } catch (error) {
            console.error('Erro ao buscar bancos:', error);
        }
    };

    const fetchAgencies = async (bankId) => {
        if (!bankId) {
            setAgencies([]);
            return;
        }
        try {
            const response = await api.get(`v1/apps/${appId}/bank-agencies/?bank=${bankId}`);
            setAgencies(response.data || []);
        } catch (error) {
            console.error('Erro ao buscar agências:', error);
        }
    };

    const handleOpenBankDialog = () => {
        setBankForm({ bank: '', agency: '', account_number: '', account_digit: '', account_type: 'checking' });
        setAgencies([]);
        setBankDialogOpen(true);
    };

    const handleBankFormBankChange = (bankId) => {
        setBankForm((prev) => ({ ...prev, bank: bankId, agency: '' }));
        fetchAgencies(bankId);
    };

    const handleSaveBankAccount = async () => {
        try {
            await api.post(`v1/apps/${appId}/bank-accounts/`, { ...bankForm, company: id });
            setBankDialogOpen(false);
            fetchBankAccounts();
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: error.response?.data?.error || 'Erro ao criar conta bancária.' });
        }
    };

    const handleDeleteBankAccount = async (bankAccount) => {
        if (!window.confirm(`Excluir a conta bancária "${bankAccount.bank_name} - Ag ${bankAccount.agency_number}"?`)) return;
        try {
            await api.delete(`v1/apps/${appId}/bank-accounts/${bankAccount.id}/`);
            setBankAccounts((prev) => prev.filter((b) => b.id !== bankAccount.id));
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: 'Erro ao excluir conta bancária.' });
        }
    };

    const handleChange = (e) => {
        setCompany({ ...company, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!appId) return;

        setStatus({ type: '', message: '' });
        setSaving(true);

        try {
            await api.patch(`v1/apps/${appId}/companies/${id}/`, company);
            setStatus({ type: 'success', message: 'Empresa atualizada com sucesso!' });
        } catch (error) {
            console.error(error);
            setStatus({
                type: 'error',
                message: error.response?.data?.detail || error.response?.data?.error || 'Erro ao atualizar empresa.'
            });
        } finally {
            setSaving(false);
        }
    };

    const deleteCompany = async () => {
        if (!appId) return;
        if (!window.confirm(`Excluir a empresa "${company.name}"? Isso também remove suas filiais.`)) return;

        try {
            await api.delete(`v1/apps/${appId}/companies/${id}/`);
            history.push('/dashboard/config/companies/view');
        } catch (error) {
            console.error('Erro ao deletar empresa:', error);
            setStatus({ type: 'error', message: error.response?.data?.error || 'Erro ao excluir empresa.' });
        }
    };

    const handleNewBranch = () => {
        history.push('/dashboard/config/companies/branches/new', { company: { id, name: company.name } });
    };

    const handleEditBranch = (branchId) => {
        history.push(`/dashboard/config/companies/branches/${branchId}/edit`, { company: { id, name: company.name } });
    };

    const handleDeleteBranch = async (branch) => {
        if (branch.is_matriz) {
            setStatus({ type: 'error', message: 'Não é possível excluir a matriz de uma empresa.' });
            return;
        }
        if (!window.confirm(`Excluir a filial "${branch.name}"?`)) return;
        try {
            await api.delete(`v1/apps/${appId}/branches/${branch.id}/`);
            setBranches((prev) => prev.filter((b) => b.id !== branch.id));
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: 'Erro ao excluir filial.' });
        }
    };

    const backPage = () => {
        history.push('/dashboard/config/companies/view');
    };

    if (loading) return null;

    return (
        <Box>
            <PageHeader icon={IconBuilding} gradient="graphite" title={company.name || 'Empresa'} subtitle={company.cnpj || 'Editar dados da empresa'} />

            {status.message && (
                <Alert severity={status.type} sx={{ mb: 2 }}>
                    {status.message}
                </Alert>
            )}

            <GlassCard sx={{ mb: 3 }}>
                <CardContent>
                    <form onSubmit={handleSubmit}>
                        <Grid container spacing={2}>
                            {FIELDS.map((field) => (
                                <Grid item xs={12} sm={6} key={field.name}>
                                    <TextField {...field} fullWidth value={company[field.name] || ''} onChange={handleChange} variant="outlined" />
                                </Grid>
                            ))}

                            <Grid item xs={12}>
                                <Stack direction="row" justifyContent="flex-end" spacing={2}>
                                    <Button color="error" onClick={deleteCompany} startIcon={<IconTrash size={18} />}>
                                        Excluir empresa
                                    </Button>
                                    <Button onClick={backPage}>Voltar</Button>
                                    <Button type="submit" variant="contained" startIcon={<IconDeviceFloppy size={18} />} disabled={saving}>
                                        {saving ? 'Salvando...' : 'Salvar'}
                                    </Button>
                                </Stack>
                            </Grid>
                        </Grid>
                    </form>
                </CardContent>
            </GlassCard>

            <GlassCard>
                <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                            <GradientIconBadge icon={IconBuildingSkyscraper} gradient="teal" size={38} iconSize={18} />
                            <Typography variant="h6" fontWeight={700}>
                                Filiais
                            </Typography>
                        </Stack>
                        <Button variant="contained" startIcon={<IconPlus size={18} />} onClick={handleNewBranch}>
                            Nova filial
                        </Button>
                    </Stack>

                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Código</TableCell>
                                <TableCell>Nome</TableCell>
                                <TableCell>CNPJ</TableCell>
                                <TableCell>Tipo</TableCell>
                                <TableCell align="right" />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {branches.map((branch) => (
                                <TableRow key={branch.id} hover sx={{ cursor: 'pointer' }} onClick={() => handleEditBranch(branch.id)}>
                                    <TableCell>{branch.code}</TableCell>
                                    <TableCell>{branch.name}</TableCell>
                                    <TableCell>{branch.cnpj || '-'}</TableCell>
                                    <TableCell>
                                        {branch.is_matriz ? (
                                            <Chip label="Matriz" color="primary" size="small" />
                                        ) : (
                                            <Chip label="Filial" size="small" />
                                        )}
                                    </TableCell>
                                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                                        <IconButton size="small" onClick={() => handleEditBranch(branch.id)}>
                                            <IconEdit size={16} />
                                        </IconButton>
                                        <IconButton size="small" onClick={() => handleDeleteBranch(branch)} disabled={branch.is_matriz}>
                                            <IconTrash size={16} />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {branches.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={5} align="center">
                                        Nenhuma filial cadastrada ainda
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </GlassCard>

            <GlassCard sx={{ mt: 3 }}>
                <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                            <GradientIconBadge icon={IconBuildingBank} gradient="graphite" size={38} iconSize={18} />
                            <Typography variant="h6" fontWeight={700}>
                                Contas Bancárias
                            </Typography>
                        </Stack>
                        <Button variant="contained" startIcon={<IconPlus size={18} />} onClick={handleOpenBankDialog}>
                            Nova conta bancária
                        </Button>
                    </Stack>

                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Banco</TableCell>
                                <TableCell>Agência</TableCell>
                                <TableCell>Conta</TableCell>
                                <TableCell>Tipo</TableCell>
                                <TableCell align="right" />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {bankAccounts.map((bankAccount) => (
                                <TableRow key={bankAccount.id} hover>
                                    <TableCell>
                                        {bankAccount.bank_code} - {bankAccount.bank_name}
                                    </TableCell>
                                    <TableCell>{bankAccount.agency_number}</TableCell>
                                    <TableCell>
                                        {bankAccount.account_number}
                                        {bankAccount.account_digit ? `-${bankAccount.account_digit}` : ''}
                                    </TableCell>
                                    <TableCell>{ACCOUNT_TYPE_LABEL[bankAccount.account_type] || bankAccount.account_type}</TableCell>
                                    <TableCell align="right">
                                        <IconButton size="small" onClick={() => handleDeleteBankAccount(bankAccount)}>
                                            <IconTrash size={16} />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {bankAccounts.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={5} align="center">
                                        Nenhuma conta bancária cadastrada ainda
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </GlassCard>

            <Dialog open={bankDialogOpen} onClose={() => setBankDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Nova conta bancária</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            select
                            fullWidth
                            label="Banco"
                            value={bankForm.bank}
                            onChange={(e) => handleBankFormBankChange(e.target.value)}
                        >
                            {banks.map((bank) => (
                                <MenuItem key={bank.id} value={bank.id}>
                                    {bank.code} - {bank.name}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            select
                            fullWidth
                            label="Tipo de conta"
                            value={bankForm.account_type}
                            onChange={(e) => setBankForm({ ...bankForm, account_type: e.target.value })}
                        >
                            <MenuItem value="checking">Conta Corrente</MenuItem>
                            <MenuItem value="savings">Poupança</MenuItem>
                        </TextField>
                        <TextField
                            select
                            fullWidth
                            label="Agência"
                            value={bankForm.agency}
                            disabled={!bankForm.bank}
                            helperText={!bankForm.bank ? 'Selecione um banco primeiro' : undefined}
                            onChange={(e) => setBankForm({ ...bankForm, agency: e.target.value })}
                        >
                            {agencies.map((agency) => (
                                <MenuItem key={agency.id} value={agency.id}>
                                    {agency.number} {agency.name ? `- ${agency.name}` : ''}
                                </MenuItem>
                            ))}
                        </TextField>
                        <Stack direction="row" spacing={2}>
                            <TextField
                                label="Conta"
                                fullWidth
                                value={bankForm.account_number}
                                onChange={(e) => setBankForm({ ...bankForm, account_number: e.target.value })}
                            />
                            <TextField
                                label="Dígito"
                                sx={{ width: 100 }}
                                value={bankForm.account_digit}
                                onChange={(e) => setBankForm({ ...bankForm, account_digit: e.target.value })}
                            />
                        </Stack>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setBankDialogOpen(false)}>Cancelar</Button>
                    <Button variant="contained" onClick={handleSaveBankAccount}>
                        Salvar
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default EditCompany;
